// CORE INTELLIGENCE's painters (#6779; prototype `:3722-3818` the Core, `:4185-4201` and
// `:4300-4337` the floating Core): the dust field, the wireframe core sphere (5 latitudes of
// 28 points, 8 longitudes of 17), one dot per agent by role, the beams, the eye and the
// squad label, all through `CorePaint2D` so a test records what was drawn.
//
// A STILL FRAME UNDER REDUCED MOTION (owner ruling V9 #1): with `moving: false` nothing
// advances. No rotation, twinkle, blink, beam, ring sweep, wander or eye tracking, and the
// hot wire points do not step, so two still frames at different times are the same picture
// (the pin `reduced motion draws a still frame on the core and the floating core`). The
// prototype kept turning the eye toward events and the pointer here (`:3758,3795,3803,4293`).
//
// TOKENS ONLY (`fleet-live-palette.ts`, brief.md item 52): `live` is the working hue,
// `glow` the link hue, the eye socket is `fill` (the prototype's `--deep`), and the
// prototype's `#F4F5FF` / `#FFFFFF` highlights are `text` on void and `fill` on paper. No
// shadowBlur and no halo (brief.md item 55): the eye's outline is a plain stroke.

import { t } from '../i18n';
import { alpha, MONO, type Paint2D } from './fleet-live-canvas';
import {
  BEAM_LIFE_MS,
  type Beam,
  type CoreDot,
  type CoreModel,
  coreHeight,
  type DotPlan,
  dotPlan,
  type Projected,
  project,
  seeded,
  squadDirections,
  type Vec3,
} from './fleet-live-core';
import { fitText } from './fleet-live-deck';
import type { LivePalette } from './fleet-live-palette';

export type CorePaint2D = Paint2D &
  Pick<
    CanvasRenderingContext2D,
    'ellipse' | 'quadraticCurveTo' | 'createRadialGradient' | 'save' | 'restore' | 'clip'
  >;

/** The floating Core's dot budget: the real agents, at most this many dots. */
const PIP_DOTS = 360;
const TILT = 0.32;

export type ClusterCentre = { readonly x: number; readonly y: number; readonly n: number };

const DUST: readonly Vec3[] = Array.from({ length: 1_100 }, (_, i) => {
  const a = seeded(i * 3) * Math.PI * 2;
  const b = Math.acos(2 * seeded(i * 3 + 1) - 1);
  const r = 0.25 + seeded(i * 3 + 2) * 1.07;
  return { x: Math.sin(b) * Math.cos(a) * r, y: Math.cos(b) * r, z: Math.sin(b) * Math.sin(a) * r };
});

type WirePoint = Vec3 & { readonly lat?: number; readonly lon?: number };

const WIRE: readonly WirePoint[] = (() => {
  const out: WirePoint[] = [];
  for (let lat = -2; lat <= 2; lat += 1) {
    const y = lat / 2.6;
    const rad = Math.sqrt(1 - y * y);
    for (let j = 0; j < 28; j += 1) {
      const an = (j / 28) * Math.PI * 2;
      out.push({ x: Math.cos(an) * rad, y, z: Math.sin(an) * rad, lat });
    }
  }
  for (let lon = 0; lon < 8; lon += 1) {
    const an = (lon / 8) * Math.PI;
    for (let j = 1; j < 18; j += 1) {
      const ph = (j / 18) * Math.PI;
      out.push({
        x: Math.sin(ph) * Math.cos(an),
        y: Math.cos(ph),
        z: Math.sin(ph) * Math.sin(an),
        lon,
      });
    }
  }
  return out;
})();

/** The blink: 260 ms, every 5,500 to 9,000 ms (prototype `:3805`). */
function createBlink(min: number, spread: number, length: number) {
  let blink = 0;
  let next: number | null = null;
  return {
    step(now: number, dt: number): void {
      if (next === null) next = now + min + seeded(Math.floor(now)) * spread;
      if (now > next && blink === 0) {
        blink = 0.0001;
        next = now + min + seeded(Math.floor(now) + 1) * spread;
      }
      if (blink > 0) {
        blink += dt / length;
        if (blink >= 1) blink = 0;
      }
    },
    lid: (): number => (blink > 0 ? Math.abs(Math.cos(Math.min(1, blink) * Math.PI)) : 1),
  };
}

type Eye = {
  readonly cx: number;
  readonly cy: number;
  readonly ew: number;
  readonly eh: number;
  readonly lx: number;
  readonly ly: number;
  readonly lid: number;
};

function almond(ctx: CorePaint2D, e: Eye, h: number, scale = 1): void {
  ctx.beginPath();
  ctx.moveTo(e.cx - e.ew * scale, e.cy);
  ctx.quadraticCurveTo(e.cx, e.cy - h * 2 * scale, e.cx + e.ew * scale, e.cy);
  ctx.quadraticCurveTo(e.cx, e.cy + h * 2 * scale, e.cx - e.ew * scale, e.cy);
  ctx.closePath();
}

/** The eye (prototype drawEye, `:3729`), with no halo and no glow. */
export function paintEye(ctx: CorePaint2D, p: LivePalette, e: Eye): void {
  const h = e.eh * Math.max(0.06, e.lid);
  const hi = p.dark ? p.text : p.fill;
  almond(ctx, e, h);
  ctx.fillStyle = p.fill;
  ctx.fill();
  ctx.save();
  almond(ctx, e, h);
  ctx.clip();
  const ix = e.cx + e.lx;
  const iy = e.cy + e.ly;
  const ir = e.eh * 0.95;
  const ig = ctx.createRadialGradient(ix, iy, 0, ix, iy, ir);
  ig.addColorStop(0, hi);
  ig.addColorStop(0.18, p.glow);
  ig.addColorStop(0.55, p.live);
  ig.addColorStop(1, p.fill);
  ctx.fillStyle = ig;
  ctx.beginPath();
  ctx.arc(ix, iy, ir, 0, 7);
  ctx.fill();
  ctx.strokeStyle = alpha(hi, 0.12);
  ctx.beginPath();
  for (let f = 0; f < 36; f += 1) {
    const a = (f / 36) * Math.PI * 2;
    ctx.moveTo(ix + Math.cos(a) * ir * 0.3, iy + Math.sin(a) * ir * 0.3);
    ctx.lineTo(ix + Math.cos(a) * ir * 0.92, iy + Math.sin(a) * ir * 0.92);
  }
  ctx.stroke();
  ctx.fillStyle = alpha(p.fill, 0.96);
  ctx.beginPath();
  ctx.ellipse(ix, iy, ir * 0.12, ir * 0.78, 0, 0, 7);
  ctx.fill();
  ctx.fillStyle = alpha(hi, 0.85);
  ctx.beginPath();
  ctx.arc(ix - ir * 0.32, iy - ir * 0.34, Math.max(1.2, ir * 0.07), 0, 7);
  ctx.fill();
  ctx.restore();
  almond(ctx, e, h);
  ctx.strokeStyle = alpha(p.glow, 0.95);
  ctx.lineWidth = 1.6;
  ctx.stroke();
  ctx.lineWidth = 1;
  ctx.strokeStyle = alpha(p.live, 0.35);
  almond(ctx, e, h, 1.28);
  ctx.stroke();
}

/** The HUD rings around the eye; the two arcs sweep only while moving. */
function paintRings(ctx: CorePaint2D, p: LivePalette, e: Eye, sweep: number, ratio: number): void {
  const rx = e.ew * ratio;
  const ry = rx * 0.36;
  ctx.strokeStyle = alpha(p.live, 0.3);
  ctx.setLineDash([2, 6]);
  ctx.beginPath();
  ctx.ellipse(e.cx, e.cy, rx, ry, 0, 0, 7);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.strokeStyle = alpha(p.glow, 0.5);
  ctx.lineWidth = 1.4;
  for (const off of [0, Math.PI]) {
    ctx.beginPath();
    ctx.ellipse(e.cx, e.cy, rx, ry, 0, sweep + off, sweep + off + 0.7);
    ctx.stroke();
  }
  ctx.lineWidth = 1;
}

function roleColor(p: LivePalette, role: CoreDot['role']): string {
  return role === 'writing' ? p.live : role === 'reviewing' ? p.glow : p.red;
}

const BEAM_COLOR: Readonly<Record<Beam['tone'], (p: LivePalette) => string>> = {
  live: (p) => p.live,
  shipped: (p) => p.green,
  red: (p) => p.red,
};

export type CorePaintInput = {
  readonly w: number;
  readonly p: LivePalette;
  readonly model: CoreModel;
  readonly plan: DotPlan;
  /** The focused squad's index, and the hovered one's (-1 for none). */
  readonly selected: number;
  readonly hover: number;
  readonly beams: readonly Beam[];
  readonly moving: boolean;
  readonly now: number;
  readonly dt: number;
};

type Stage = { readonly cx: number; readonly cy: number; readonly r: number; readonly sx: number };

function centresOf(
  input: CorePaintInput,
  pts: readonly (Projected & { dot: CoreDot })[],
  c: Stage,
  rot: number,
) {
  const dirs = squadDirections(input.model.squads.length);
  const sums = dirs.map(() => ({ x: 0, y: 0, n: 0 }));
  for (const pr of pts) {
    const s = sums[pr.dot.squad];
    if (s === undefined) continue;
    s.x += pr.x;
    s.y += pr.y;
    s.n += 1;
  }
  // A squad with no dots (no bucket yet) is still a cluster: its direction, projected.
  return sums.map((s, i): ClusterCentre => {
    if (s.n > 0) return { x: s.x / s.n, y: s.y / s.n, n: s.n };
    const d = dirs[i] ?? { x: 0, y: 1, z: 0 };
    const pr = project({ x: d.x * 0.8, y: d.y * 0.8, z: d.z * 0.8 }, c, rot, TILT);
    return { x: pr.x, y: pr.y, n: 1 };
  });
}

/** The labels' inset from the canvas edge, and their 10 px font's height. */
const LABEL_EDGE = 6;
const LABEL_H = 10;

/**
 * The selected squad's label: a leader from the cluster to an elbow 40 px out, then the
 * name on the side with room for it. INSIDE THE CANVAS (the bounds guard,
 * `fleet-live-core-bounds.test.ts`): the elbow is held inside, the label takes the other
 * side when its own has less room, and a name longer than the room is cut with an
 * ellipsis; the prototype ran a long name, or one near the edge, off the canvas.
 */
function paintLabel(
  ctx: CorePaint2D,
  p: LivePalette,
  c: ClusterCentre,
  box: { readonly w: number; readonly h: number; readonly cx: number },
  name: string,
  ring: number,
): void {
  const full = t('fleet.live.squad_title', { name }).toUpperCase();
  ctx.font = `600 10px ${MONO}`;
  const measure = (s: string): number => ctx.measureText(s).width;
  const elbow = (right: boolean): number =>
    Math.max(LABEL_EDGE, Math.min(box.w - LABEL_EDGE, c.x + (right ? 40 : -40)));
  const room = (right: boolean): number =>
    right ? box.w - LABEL_EDGE - (elbow(true) + 6) : elbow(false) - 6 - LABEL_EDGE;
  const want = c.x > box.cx;
  const right = measure(full) <= room(want) || room(want) >= room(!want) ? want : !want;
  const lx = elbow(right);
  const txt = fitText(full, Math.max(0, room(right)), measure);
  const tw = measure(txt);
  const ly = Math.max(LABEL_EDGE + LABEL_H + 3, Math.min(box.h - LABEL_EDGE, c.y - 30));
  ctx.strokeStyle = alpha(p.glow, 0.7);
  ctx.beginPath();
  ctx.moveTo(c.x, c.y);
  ctx.lineTo(lx, ly);
  ctx.lineTo(lx + (right ? tw + 12 : -tw - 12), ly);
  ctx.stroke();
  ctx.strokeStyle = alpha(p.glow, 0.8);
  ctx.beginPath();
  ctx.arc(c.x, c.y, ring, 0, 7);
  ctx.stroke();
  ctx.fillStyle = p.text;
  ctx.textAlign = right ? 'left' : 'right';
  ctx.textBaseline = 'bottom';
  ctx.fillText(txt, lx + (right ? 6 : -6), ly - 3);
}

export type CorePainter = {
  /** Paint one frame; answers the cluster centres, for the click and hover hit test. */
  readonly paint: (ctx: CorePaint2D, input: CorePaintInput) => readonly ClusterCentre[];
};

type Placed = Projected & { readonly dot: CoreDot };

function paintDust(ctx: CorePaint2D, p: LivePalette, c: Stage, rot: number): void {
  for (const d of DUST) {
    const pr = project(d, { ...c, r: c.r * 1.05 }, rot * 0.6, TILT);
    ctx.fillStyle = alpha(p.live, (p.dark ? 0.16 : 0.2) * pr.k);
    ctx.fillRect(pr.x, pr.y, 1.1, 1.1);
  }
}

/** The dots on one side of the core (`front`: nearer than the eye), by role. */
function paintDots(
  ctx: CorePaint2D,
  input: CorePaintInput,
  pts: readonly Placed[],
  front: boolean,
): void {
  for (const pr of pts) {
    if (pr.z <= 0 !== front) continue;
    const { dot } = pr;
    const hot = dot.squad === input.selected || dot.squad === input.hover;
    const tw = input.moving ? 0.75 + 0.25 * Math.sin((input.now / 600) * dot.sp + dot.ph) : 1;
    const a = 0.82 * tw * (0.45 + 0.55 * pr.k) * (hot ? 1.35 : 0.75);
    const s = 1.8 * pr.k * (dot.squad === input.selected ? 1.3 : 1);
    ctx.fillStyle = alpha(roleColor(input.p, dot.role), Math.min(1, a));
    ctx.fillRect(pr.x - s / 2, pr.y - s / 2, s, s);
  }
}

/** A hovered cluster that is not the selected one: a dashed ring and its name. */
function paintHover(
  ctx: CorePaint2D,
  input: CorePaintInput,
  centres: readonly ClusterCentre[],
): void {
  const hov = centres[input.hover];
  const squad = input.model.squads[input.hover];
  if (hov === undefined || squad === undefined || input.hover === input.selected) return;
  ctx.strokeStyle = alpha(input.p.glow, 0.5);
  ctx.setLineDash([2, 3]);
  ctx.beginPath();
  ctx.arc(hov.x, hov.y, 12, 0, 7);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.font = `500 10px ${MONO}`;
  ctx.fillStyle = input.p.t2;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';
  // Inside the canvas, as the selected label is: cut to its width, centred within it.
  const measure = (s: string): number => ctx.measureText(s).width;
  const txt = fitText(squad.name.toUpperCase(), input.w - 2 * LABEL_EDGE, measure);
  const half = measure(txt) / 2;
  const x = Math.max(LABEL_EDGE + half, Math.min(input.w - LABEL_EDGE - half, hov.x));
  const y = Math.max(LABEL_EDGE + LABEL_H, Math.min(coreHeight(input.w) - LABEL_EDGE, hov.y - 16));
  ctx.fillText(txt, x, y);
}

export function createCorePainter(): CorePainter {
  let rot = 0;
  let look = { x: 0, y: 0 };
  let target = { x: 0, y: 0 };
  const blink = createBlink(5_500, 3_500, 260);
  return {
    paint(ctx, input) {
      const { w, p, moving, now, dt } = input;
      const h = coreHeight(w);
      ctx.clearRect(0, 0, w, h);
      const c: Stage = {
        cx: w / 2,
        cy: h / 2 + 14,
        r: Math.min(w * 0.33, h * 0.4),
        sx: w > 520 ? 1.3 : 1.12,
      };
      if (moving) rot += dt * 0.00009;
      const clock = moving ? now : 0;
      paintDust(ctx, p, c, rot);
      paintWire(ctx, p, { ...c, r: c.r * 0.56, sx: 1 }, -rot * 1.8, clock);
      const pts: Placed[] = input.plan.dots.map((dot) => ({ ...project(dot, c, rot, TILT), dot }));
      const centres = centresOf(input, pts, c, rot);
      paintDots(ctx, input, pts, false);
      if (moving) target = paintBeams(ctx, p, input, pts, centres, c) ?? target;
      const ew = Math.max(26, c.r * 0.27);
      const eye = { cx: c.cx, cy: c.cy, ew, eh: ew * 0.42 };
      paintRings(ctx, p, { ...eye, lx: 0, ly: 0, lid: 1 }, clock / 2200, 1.75);
      if (moving) {
        const lx = Math.max(-ew * 0.3, Math.min(ew * 0.3, target.x * 0.08));
        const ly = Math.max(-eye.eh * 0.22, Math.min(eye.eh * 0.22, target.y * 0.08));
        const k = Math.min(1, dt * 0.006);
        look = { x: look.x + (lx - look.x) * k, y: look.y + (ly - look.y) * k };
        blink.step(now, dt);
      }
      paintEye(ctx, p, { ...eye, lx: look.x, ly: look.y, lid: moving ? blink.lid() : 1 });
      paintDots(ctx, input, pts, true);
      const sel = centres[input.selected];
      const squad = input.model.squads[input.selected];
      if (sel !== undefined && squad !== undefined) {
        paintLabel(
          ctx,
          p,
          sel,
          { w, h, cx: c.cx },
          squad.name,
          10 + (moving ? 2 * Math.sin(now / 300) : 0),
        );
      }
      paintHover(ctx, input, centres);
      return centres;
    },
  };
}

function paintWire(ctx: CorePaint2D, p: LivePalette, c: Stage, rot: number, clock: number): void {
  const pr = WIRE.map((wp) => project(wp, c, rot, TILT));
  ctx.strokeStyle = alpha(p.live, p.dark ? 0.2 : 0.24);
  ctx.beginPath();
  WIRE.forEach((wp, i) => {
    const a = pr[i];
    if (a === undefined) return;
    const next = WIRE[i + 1];
    if (wp.lat !== undefined) {
      const b = next?.lat === wp.lat ? pr[i + 1] : pr[i - 27];
      if (b === undefined) return;
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
    } else if (next?.lon !== undefined && next.lon === wp.lon) {
      const b = pr[i + 1];
      if (b === undefined) return;
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
    }
  });
  ctx.stroke();
  const step = Math.floor(clock / 400);
  pr.forEach((q, i) => {
    const hot = (i * 13 + step) % 23 === 0;
    const s = hot ? 3 : 1.5;
    ctx.fillStyle = alpha(hot ? p.glow : p.live, (hot ? 0.9 : 0.35) * (q.z < 0 ? 1 : 0.5));
    ctx.fillRect(q.x - s / 2, q.y - s / 2, s, s);
  });
}

/** Draw the live beams; answers where the newest young beam leaves from, for the eye. */
function paintBeams(
  ctx: CorePaint2D,
  p: LivePalette,
  input: CorePaintInput,
  pts: readonly (Projected & { dot: CoreDot })[],
  centres: readonly ClusterCentre[],
  c: Stage,
): { x: number; y: number } | null {
  let look: { x: number; y: number } | null = null;
  for (const b of input.beams) {
    const own = pts.filter((q) => q.dot.squad === b.squad);
    const from = own.length > 0 ? own[b.seed % own.length] : centres[b.squad];
    if (from === undefined) continue;
    const k = (input.now - b.born) / BEAM_LIFE_MS;
    const al = k < 0.15 ? k / 0.15 : 1 - (k - 0.15) / 0.85;
    const color = BEAM_COLOR[b.tone](p);
    ctx.strokeStyle = alpha(color, 0.35 * al);
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(c.cx, c.cy);
    ctx.stroke();
    const t2 = (k * 1.6) % 1;
    ctx.fillStyle = alpha(color, al);
    ctx.beginPath();
    ctx.arc(from.x + (c.cx - from.x) * t2, from.y + (c.cy - from.y) * t2, 1.8, 0, 7);
    ctx.fill();
    ctx.fillStyle = alpha(color, 0.9 * al);
    ctx.fillRect(from.x - 2, from.y - 2, 4, 4);
    if (k < 0.2) look = { x: from.x - c.cx, y: from.y - c.cy };
  }
  return look;
}

// ─── The floating Core ────────────────────────────────────────────────────────

export type PipPointer = {
  /** The pointer's offset from the eye's centre, in CSS px. */
  readonly dx: number;
  readonly dy: number;
  /** When it last moved, on the frame clock. */
  readonly at: number;
};

export type PipPaintInput = {
  readonly w: number;
  readonly h: number;
  /** Collapsed: the eye alone, in the pill. */
  readonly mini: boolean;
  readonly p: LivePalette;
  readonly model: CoreModel;
  readonly moving: boolean;
  readonly now: number;
  readonly dt: number;
  readonly pointer: PipPointer | null;
};

/** How long after the pointer last moved the eye still follows it. */
export const PIP_FOLLOW_MS = 5_000;

export function createPipPainter(): {
  readonly paint: (ctx: CorePaint2D, input: PipPaintInput) => void;
} {
  let rot = 0;
  let look = { x: 0, y: 0 };
  let planOf: { model: CoreModel; plan: DotPlan } | null = null;
  const blink = createBlink(3_500, 4_500, 240);
  return {
    paint(ctx, input) {
      const { w, h, p, moving, now, dt, mini } = input;
      ctx.clearRect(0, 0, w, h);
      if (planOf?.model !== input.model)
        planOf = { model: input.model, plan: dotPlan(input.model.squads, PIP_DOTS) };
      const r = mini ? Math.min(w * 0.75, h * 1.7) : Math.min(w * 0.4, h * 0.5);
      const c: Stage = { cx: w / 2, cy: h / 2 + 2, r, sx: 1.35 };
      if (moving) rot += dt * 0.00014;
      const pts = planOf.plan.dots.map((d) => ({ ...project(d, c, rot, TILT), d }));
      const dots = (front: boolean): void => {
        for (const o of pts) {
          if (o.z <= 0 !== front) continue;
          const tw = moving ? 0.7 + 0.3 * Math.sin(now / 500 + o.d.ph) : 1;
          ctx.fillStyle = alpha(roleColor(p, o.d.role), 0.85 * tw * (0.4 + 0.6 * o.k));
          const s = 1.6 * o.k;
          ctx.fillRect(o.x - s / 2, o.y - s / 2, s, s);
        }
      };
      if (!mini) dots(false);
      const ew = r * 0.46;
      const eye = { cx: c.cx, cy: c.cy, ew, eh: ew * 0.42 };
      paintRings(ctx, p, { ...eye, lx: 0, ly: 0, lid: 1 }, moving ? now / 1600 : 0, 1.6);
      if (moving) {
        const ptr = input.pointer;
        const k = Math.min(1, dt * 0.012 || 0.2) * 1.6;
        let tx: number;
        let ty: number;
        if (ptr !== null && now - ptr.at < PIP_FOLLOW_MS) {
          const d = Math.hypot(ptr.dx, ptr.dy) || 1;
          const m = Math.min(1, d / 160);
          tx = (ptr.dx / d) * ew * 0.34 * m;
          ty = (ptr.dy / d) * eye.eh * 0.32 * m;
        } else {
          tx = Math.sin(now / 1700) * ew * 0.16;
          ty = Math.cos(now / 2300) * eye.eh * 0.12;
        }
        look = {
          x: look.x + (tx - look.x) * Math.min(1, k),
          y: look.y + (ty - look.y) * Math.min(1, k),
        };
        blink.step(now, dt);
      }
      paintEye(ctx, p, { ...eye, lx: look.x, ly: look.y, lid: moving ? blink.lid() : 1 });
      if (mini) return;
      dots(true);
      ctx.strokeStyle = alpha(p.live, 0.5);
      ctx.beginPath();
      for (const [x, y, sx, sy] of [
        [8, 8, 1, 1],
        [w - 8, 8, -1, 1],
        [8, h - 8, 1, -1],
        [w - 8, h - 8, -1, -1],
      ] as const) {
        ctx.moveTo(x, y + 8 * sy);
        ctx.lineTo(x, y);
        ctx.lineTo(x + 8 * sx, y);
      }
      ctx.stroke();
    },
  };
}
